import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register the plugin (standard practice, even if using IntersectionObserver for the trigger)
gsap.registerPlugin(ScrollTrigger);

const CirclePoints = ({ heading, points, className }) => {
    const sectionRef = useRef(null);
    const headingRef = useRef(null);
    const itemsRef = useRef([]); // Array ref for the list items
    const animationTriggered = useRef(false);

    useEffect(() => {
        // 1. Setup the Intersection Observer (Same logic as your reference)
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !animationTriggered.current) {
                        animationTriggered.current = true;
                        animateSection();
                        if (sectionRef.current) {
                            observer.unobserve(sectionRef.current);
                        }
                    }
                });
            },
            {
                threshold: 0.1, // Trigger when 10% of the component is visible
            }
        );

        const currentRef = sectionRef.current;

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, []);

    const animateSection = () => {
        const tl = gsap.timeline({ defaults: { duration: 0.6, ease: "power2.out" } });

        // 2. Animate the Heading
        tl.fromTo(
            headingRef.current,
            { y: 30, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.5 }
        );

        // 3. Animate the List Items (staggered)
        // We filter itemsRef.current to ensure we don't animate null refs
        if (itemsRef.current.length > 0) {
            tl.fromTo(
                itemsRef.current,
                { y: 50, opacity: 0 },
                { 
                    y: 0, 
                    opacity: 1, 
                    stagger: 0.15, // Delay between each item appearing
                    duration: 0.5 
                },
                "-=0.3" // Start this animation slightly before the heading finishes
            );
        }
    };

    return (
        <section 
            ref={sectionRef} 
            className='container max-w-7xl mx-auto px-6 md:px-12 flex flex-col gap-y-10 my-28'
        >
            <h2 
                ref={headingRef} 
                className='font-[600] text-[34px] sm:text-[40px] leading-[38px] sm:leading-[42px] text-center opacity-0 translate-y-4' // Default hidden state to prevent flash
            >
                {heading}
            </h2>
            
            <ol className={className}>
                {points.map(({ img, figCaption, description }, index) => (
                    <li 
                        key={index} 
                        ref={(el) => (itemsRef.current[index] = el)} // Assign refs to array
                        className="opacity-0 translate-y-8" // Default hidden state
                    >
                        <article className="flex flex-col items-center justify-center">
                            <div className='flex flex-col gap-y-8 items-center rounded-full justify-center w-56 h-56 bg-[#03C0F24D] p-10'>
                                <figure className='relative overflow-hidden w-full h-full flex justify-center items-center'>
                                    <img src={img} className='object-scale-down absolute w-full h-full' alt={figCaption} />
                                </figure>
                                <figcaption className='rounded-full whitespace-pre-line flex items-center justify-center'>
                                    <div className='text-center font-[500] text-[16px] text-[#000000]'>
                                        {figCaption}
                                    </div>
                                </figcaption>
                            </div>
                            <h4 className="text-[20px] text-center w-[100%] text-[#000000] font-[600] mt-4 sm:mt-9 px-10">
                                {description}
                            </h4>
                        </article>
                    </li>
                ))}
            </ol>
        </section>
    );
}

export default CirclePoints;